import { useEffect, useState } from 'react';
import { ChevronDown, ListChecks, Plus, Trash2 } from 'lucide-react';
import { ApiError } from '@/api/client';
import {
  useAiUseCases,
  useAiUseCaseTemplates,
  useApplyAiUseCaseTemplate,
  useSaveAiUseCases,
  type AiUseCase,
} from '@/api/use-cases';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';

const lines = (v: string) =>
  v
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
const list = (v: string) =>
  v
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean);

const slug = (title: string, taken: string[]) => {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 36) || 'use_case';
  let id = base;
  for (let n = 2; taken.includes(id); n++) id = `${base}_${n}`;
  return id;
};

/**
 * Settings → AI agents → Use cases: the procedures every AI agent follows for each kind of request —
 * what to check, how to reply, when to hand over. Starts from a template; fully editable.
 */
export function UseCasesPanel() {
  const toast = useToast();
  const settings = useAiUseCases();
  const templates = useAiUseCaseTemplates();
  const save = useSaveAiUseCases();
  const apply = useApplyAiUseCaseTemplate();
  const [draft, setDraft] = useState<AiUseCase[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    if (settings.data) {
      setDraft(settings.data.useCases);
      setTemplateId(settings.data.templateId ?? 'general');
    }
  }, [settings.data]);

  if (settings.isPending || !draft) return <Skeleton className="mt-8 h-64 rounded-2xl" />;
  if (settings.error)
    return (
      <Card className="mt-8">
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      </Card>
    );

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.data!.useCases);
  const update = (i: number, patch: Partial<AiUseCase>) =>
    setDraft((d) => d!.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  const onError = (e: Error) => {
    const fields = e instanceof ApiError ? e.fieldErrors : {};
    toast.error(Object.values(fields)[0] ?? e.message);
  };

  return (
    <Card className="mt-8 overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-100 p-5 lg:flex-row lg:items-end">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 font-semibold text-slate-900">
            <ListChecks className="text-brand-500 h-4 w-4" /> Use cases
          </p>
          <p className="mt-1 text-sm text-slate-500">
            How your AI agents handle each kind of request: they first work out what the customer needs, check
            their account with your skills, then reply — and hand over to your team when a use case says so.
            {!settings.data!.customized && ' Using the general template until you save.'}
          </p>
        </div>
        <div className="flex shrink-0 items-end gap-2">
          <Select
            label="Start from a template"
            wrapperClassName="w-56"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button
            variant="secondary"
            loading={apply.isPending}
            onClick={() => {
              if (!window.confirm('Replace your use cases with this template?')) return;
              apply.mutate(templateId, {
                onSuccess: () => (setOpen(null), toast.success('Template applied')),
                onError,
              });
            }}
          >
            Apply
          </Button>
        </div>
      </div>
      {templates.data && (
        <p className="border-b border-slate-100 bg-slate-50/60 px-5 py-2 text-xs text-slate-500">
          {templates.data.find((t) => t.id === templateId)?.description}
        </p>
      )}

      <ul className="divide-y divide-slate-100">
        {draft.map((u, i) => {
          const expanded = open === i;
          return (
            <li key={`${u.id}-${i}`} className={cn(u.enabled === false && 'bg-slate-50/60')}>
              <div className="flex items-center gap-3 px-5 py-3">
                <Switch size="sm" checked={u.enabled !== false} onChange={(v) => update(i, { enabled: v })} />
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : i)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-sm font-medium',
                        u.enabled === false ? 'text-slate-400' : 'text-slate-800',
                      )}
                    >
                      {u.title}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {[...u.categories, ...u.signals.slice(0, 5)].join(' · ') || 'No triggers yet'}
                    </span>
                  </span>
                  {u.handoff.length > 0 && <Badge>Hands over</Badge>}
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-slate-400 transition-transform',
                      expanded && 'rotate-180',
                    )}
                  />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${u.title}`}
                  onClick={() => (setDraft((d) => d!.filter((_, j) => j !== i)), setOpen(null))}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {expanded && (
                <div className="grid gap-4 border-t border-slate-100 bg-white px-5 py-4 lg:grid-cols-2">
                  <Input
                    label="Title"
                    value={u.title}
                    onChange={(e) => update(i, { title: e.target.value })}
                  />
                  <Input
                    label="Categories"
                    hint="Ticket or issue-form categories, comma-separated."
                    defaultValue={u.categories.join(', ')}
                    onBlur={(e) => update(i, { categories: list(e.target.value) })}
                  />
                  <Textarea
                    label="Words that signal it"
                    hint="Comma-separated, e.g. refund, cancel, charged twice. Any language."
                    rows={2}
                    wrapperClassName="lg:col-span-2"
                    defaultValue={u.signals.join(', ')}
                    onBlur={(e) => update(i, { signals: list(e.target.value).map((s) => s.toLowerCase()) })}
                  />
                  <Textarea
                    label="What to check first"
                    hint="One step per line — mention the skill tools to use."
                    rows={4}
                    defaultValue={u.check.join('\n')}
                    onBlur={(e) => update(i, { check: lines(e.target.value) })}
                  />
                  <Textarea
                    label="How to reply"
                    hint="One point per line."
                    rows={4}
                    defaultValue={u.reply.join('\n')}
                    onBlur={(e) => update(i, { reply: lines(e.target.value) })}
                  />
                  <Textarea
                    label="Hand over to a person when…"
                    hint="One situation per line. Leave empty if the AI can always finish it."
                    rows={3}
                    wrapperClassName="lg:col-span-2"
                    defaultValue={u.handoff.join('\n')}
                    onBlur={(e) => update(i, { handoff: lines(e.target.value) })}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div
        className={cn(
          'flex flex-wrap items-center gap-2 border-t px-5 py-3',
          dirty ? 'border-amber-200 bg-amber-50/95' : 'border-slate-100 bg-slate-50/90',
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          disabled={draft.length >= 30}
          onClick={() => {
            const title = 'New use case';
            setDraft((d) => [
              ...d!,
              {
                id: slug(
                  title,
                  d!.map((x) => x.id),
                ),
                title,
                enabled: true,
                categories: [],
                signals: [],
                check: ['Look this up with the account tools before answering.'],
                reply: ['Answer with the facts you found and the next step.'],
                handoff: [],
              },
            ]);
            setOpen(draft.length);
          }}
        >
          <Plus className="h-4 w-4" /> Add use case
        </Button>
        <span className="ml-auto flex items-center gap-2">
          {dirty && (
            <>
              <span className="text-sm font-medium text-amber-800">Unsaved changes</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (setDraft(settings.data!.useCases), setOpen(null))}
              >
                Discard
              </Button>
            </>
          )}
          <Button
            size="sm"
            disabled={!dirty && settings.data!.customized}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { useCases: draft, templateId: settings.data!.templateId },
                { onSuccess: () => toast.success('Use cases saved'), onError },
              )
            }
          >
            Save use cases
          </Button>
        </span>
      </div>
    </Card>
  );
}
